import { useCallback, useEffect, useState } from 'react'
import { api, endpoints, type Account } from '../api/client'
import { AccountRow } from '../components/AccountRow'
import { ClearHistoryCard } from '../components/ClearHistory'
import { CloseAccountCard } from '../components/CloseAccount'
import { isNativeApp, nativePlatform } from '../native'

import { Sheet } from '../components/Sheet'

/*
  Settings: the account, rather than the business.

  Split out of Company deliberately. Everything on Company is something the board reads —
  the trade, the headcount, the figures, the documents. None of that is true of a password
  or of who else can sign in, and an owner looking for either was hunting through a screen
  whose whole purpose is the profile.

  Every line here states what is currently true and carries at most one button. The forms
  open in a sheet. The first version put all of them on the page at once, which meant the
  screen was mostly five empty boxes and you had to scroll past a password field you were
  not changing to find out who else could sign in. That does not scale, and this area is
  going to grow.
*/

interface Member {
  id: string
  email: string
  displayName: string | null
  role: string
  createdAt: string
  lastLoginAt: string | null
  emailVerifiedAt: string | null
}

interface Invite {
  id: string
  email: string
  expiresAt: string
  createdAt: string
  invitedByName: string | null
  expired: boolean
}

export function Settings({ account, extra }: { account: Account; extra?: React.ReactNode }) {
  return (
    <>
      <h1 className="screen-title">Settings</h1>

      {/* Two abreast above 1080px, one below. Six independent cards with nothing to read
          across between them, which is what makes the second column free. */}
      <div className="colstack">
        <Login account={account} />
        {/* Billing is passed in rather than imported, so this screen does not have to know
            how subscriptions work in order to render a password row. */}
        {extra}
        <Team accountEmail={account.email} />
        <ClearHistoryCard role={account.role} />
        <CloseAccountCard />
        <Build />
      </div>
    </>
  )
}

/*
  Which code this is.

  It exists because of the iOS loop. A pull updates the TypeScript, but the WebView keeps
  running whatever was last copied into the native project, so a missed "cap sync" looks
  exactly like a fix that did not work: the app builds, installs, runs, and nothing anywhere
  says it is stale. We lost most of an afternoon to that. This makes it a two-second check.

  The sha is stamped in by vite.config.ts at build time. A trailing + means the build came
  off a working tree with uncommitted changes, which on a phone is the difference between
  "this is the pushed fix" and "this is whatever was on that laptop at the time".
*/
function Build() {
  const time = new Date(__BUILD_TIME__)
  const built = time.toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  })

  return (
    <div className="card card--rows">
      <div className="card__title">This build</div>
      <AccountRow
        label={__BUILD_SHA__}
        note={isNativeApp() ? `Built ${built} · ${nativePlatform()} app` : `Built ${built}`}
      />
    </div>
  )
}

/* ── How you get in ──/* ── How you get in ──────────────────────────────────────────────────────── */

function Login({ account }: { account: Account }) {
  const [changing, setChanging] = useState(false)
  const [resent, setResent] = useState(false)
  const [done, setDone] = useState(false)

  const resend = async () => {
    setResent(true)
    try { await endpoints.resendVerification(account.email) } catch { /* it says sent either way */ }
  }

  return (
    <div className="card card--rows">
      <div className="card__title">How you sign in</div>

      <AccountRow
        label={account.email}
        note={
          account.emailVerified
            ? 'Confirmed. Password resets and board digests go here.'
            : resent
              ? 'Sent. Follow the link in it to confirm this address.'
              : 'Not confirmed yet. Until it is, we cannot be sure resets reach you.'
        }
        action={!account.emailVerified && !resent && (
          <button className="btn btn--small" onClick={() => void resend()}>Resend</button>
        )}
      />

      <AccountRow
        label="Password"
        note={done
          ? 'Changed. Your other devices stay signed in.'
          : 'Changing it does not sign your other devices out.'}
        action={
          <button className="btn btn--small" onClick={() => setChanging(true)}>Change</button>
        }
      />

      {changing && (
        <ChangePassword
          onClose={() => setChanging(false)}
          onDone={() => { setChanging(false); setDone(true) }}
        />
      )}
    </div>
  )
}

/*
  Changing the password from inside the product, for somebody who knows their current one.
  The reset link is the other route and lives on the sign-in card, because that is where you
  are when you cannot get in.
*/
function ChangePassword({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    setError(null)
    if (next !== again) { setError('Those two do not match.'); return }

    setBusy(true)
    try {
      await endpoints.changePassword(current, next)
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change it')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet title="Change your password" onClose={onClose}>
      <form className="pw" onSubmit={submit}>
        {error && <div className="signin__error">{error}</div>}

        <label className="field">
          <span className="field__label">Current password</span>
          <input className="input" type="password" autoComplete="current-password" required
                 value={current} onChange={e => setCurrent(e.target.value)} />
        </label>

        <label className="field">
          <span className="field__label">New password</span>
          <input className="input" type="password" autoComplete="new-password" required minLength={8}
                 value={next} onChange={e => setNext(e.target.value)} />
          <span className="field__hint">At least 8 characters.</span>
        </label>

        <label className="field">
          <span className="field__label">Again</span>
          <input className="input" type="password" autoComplete="new-password" required
                 value={again} onChange={e => setAgain(e.target.value)} />
        </label>

        <div className="sheet__actions">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" disabled={busy}>
            {busy ? 'Changing' : 'Change password'}
          </button>
        </div>
      </form>
    </Sheet>
  )
}

/* ── Who else can reach this board ───────────────────────────────────────── */

function Team({ accountEmail }: { accountEmail: string }) {
  const [members, setMembers] = useState<Member[]>([])
  const [invites, setInvites] = useState<Invite[]>([])
  const [inviting, setInviting] = useState(false)
  const [sent, setSent] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const d = await api.get<{ members: Member[]; invites: Invite[] }>('/api/team')
      setMembers(d.members)
      setInvites(d.invites)
    } catch {
      setMembers([]); setInvites([])
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const revoke = async (id: string) => {
    await api.del(`/api/team/invites/${id}`)
    await load()
  }

  return (
    <div className="card card--rows">
      <div className="card__head">
        <div className="card__title">People</div>
        <button className="btn btn--small" onClick={() => setInviting(true)}>Invite somebody</button>
      </div>

      {members.map(m => (
        <AccountRow
          key={m.id}
          label={m.displayName || m.email}
          tag={m.email === accountEmail ? 'you' : undefined}
          note={
            <>
              {m.displayName ? `${m.email} · ` : ''}{m.role}
              {m.lastLoginAt ? ` · last in ${when(m.lastLoginAt)}` : ' · never signed in'}
              {!m.emailVerifiedAt && ' · email unconfirmed'}
            </>
          }
        />
      ))}

      {invites.map(i => (
        <AccountRow
          key={i.id}
          muted
          label={i.email}
          note={
            <>
              {i.expired ? 'Invitation expired' : `Invited ${when(i.createdAt)}, not accepted yet`}
              {i.invitedByName ? ` · by ${i.invitedByName}` : ''}
            </>
          }
          action={
            <button className="btn btn--small btn--danger" onClick={() => void revoke(i.id)}>
              {i.expired ? 'Remove' : 'Withdraw'}
            </button>
          }
        />
      ))}

      {sent && <div className="signin__note">Invitation sent to {sent}.</div>}

      {inviting && (
        <InviteSomebody
          onClose={() => setInviting(false)}
          onSent={async email => { setInviting(false); setSent(email); await load() }}
        />
      )}
    </div>
  )
}

/*
  There are no roles behind this. Everything in the product is scoped to the business, so a
  second person can do what the first can: ask the board, put things on the table, upload a
  statement. That is said here, in the sheet, at the moment somebody is deciding who to
  invite — rather than as a standing paragraph on a screen they opened to change a password.
*/
function InviteSomebody(
  { onClose, onSent }: { onClose: () => void; onSent: (email: string) => Promise<void> },
) {
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy || !email.trim()) return
    setBusy(true); setError(null)
    try {
      await api.post('/api/team/invites', { email: email.trim() })
      await onSent(email.trim())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send that')
      setBusy(false)
    }
  }

  return (
    <Sheet title="Invite somebody" onClose={onClose}>
      <p className="muted" style={{ marginBottom: 'var(--space-4)' }}>
        Whoever you invite can do what you can: ask the board, put things on the table and
        upload statements. There are no read-only seats. This is who you trust with the
        company, not who you trust with a report.
      </p>

      <form className="pw" onSubmit={submit}>
        {error && <div className="signin__error">{error}</div>}

        <label className="field">
          <span className="field__label">Their email address</span>
          <input className="input" type="email" placeholder="their@email.com" required
                 value={email} onChange={e => setEmail(e.target.value)} />
          <span className="field__hint">
            They set their own password. The link expires in seven days.
          </span>
        </label>

        <div className="sheet__actions">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" disabled={busy || !email.trim()}>
            {busy ? 'Sending' : 'Send the invitation'}
          </button>
        </div>
      </form>
    </Sheet>
  )
}

/** "3 days ago", roughly. Precision here is noise. */
function when(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days} days ago`
  const months = Math.floor(days / 30)
  return months === 1 ? 'a month ago' : `${months} months ago`
}
